/**
 * Repeatable-FORM instance strategy picker (long / wide / aggregate / pinned).
 * Shown only for repeatable forms. Edits spec.instanceStrategy.
 */
import { Repeat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import {
  WIDE_DEFAULT_CAP,
  type FormInstanceStrategy,
  type FormInstanceMode,
  type AggregateFn,
} from '../types/data-view-spec'

const MODE_LABELS: Record<FormInstanceMode, string> = {
  long: 'هر نمونه یک ردیف (طولی)',
  wide: 'ستون‌های جداگانه برای هر نمونه (عرضی)',
  aggregate: 'تجمیع نمونه‌ها',
  pinned: 'فقط یک نمونه مشخص',
}

const AGG_LABELS: Record<AggregateFn, string> = {
  count: 'تعداد', first: 'اولین', last: 'آخرین', min: 'کمینه', max: 'بیشینه', avg: 'میانگین',
}

export function InstanceStrategyPicker({
  value, onChange,
}: { value: FormInstanceStrategy; onChange: (s: FormInstanceStrategy) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Repeat className="w-3.5 h-3.5" />
          نمونه‌های تکرارشونده
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <p className="text-sm font-medium">نحوهٔ نمایش نمونه‌های تکرارشونده</p>
        <div className="space-y-1">
          {(Object.keys(MODE_LABELS) as FormInstanceMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => onChange({ ...value, mode })}
              className={cn(
                'w-full text-start text-sm px-2.5 py-1.5 rounded-md transition-colors',
                value.mode === mode ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>

        {value.mode === 'wide' && (
          <label className="block text-sm">
            <span className="text-muted-foreground">حداکثر تعداد ستون نمونه</span>
            <Input
              type="number" min={1} max={50}
              value={value.cap ?? WIDE_DEFAULT_CAP}
              onChange={(e) => onChange({ ...value, cap: Math.max(1, Number(e.target.value) || WIDE_DEFAULT_CAP) })}
              className="mt-1 h-8"
            />
          </label>
        )}

        {value.mode === 'pinned' && (
          <label className="block text-sm">
            <span className="text-muted-foreground">شمارهٔ نمونه</span>
            <Input
              type="number" min={1}
              value={value.pinnedIndex ?? 1}
              onChange={(e) => onChange({ ...value, pinnedIndex: Math.max(1, Number(e.target.value) || 1) })}
              className="mt-1 h-8"
            />
          </label>
        )}

        {value.mode === 'aggregate' && (
          <label className="block text-sm">
            <span className="text-muted-foreground">تابع تجمیع</span>
            <Select
              value={value.aggregate ?? 'last'}
              onValueChange={(v) => onChange({ ...value, aggregate: v as AggregateFn })}
            >
              <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(AGG_LABELS) as AggregateFn[]).map((fn) => (
                  <SelectItem key={fn} value={fn}>{AGG_LABELS[fn]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground/70">
              کمینه/بیشینه/میانگین فقط روی فیلدهای عددی اعمال می‌شود.
            </p>
          </label>
        )}
      </PopoverContent>
    </Popover>
  )
}
